// ─────────────────────────────────────────────
//  src/lib/whatsapp/sendWhatsApp.js
//  SendZen WhatsApp + Firestore + Google Sheets
// ─────────────────────────────────────────────

import { db } from '../../firebase'
import { collection, addDoc, serverTimestamp } from 'firebase/firestore'

// ── SendZen config ────────────────────────────
const SENDZEN_CONFIG = {
    API_URL: "https://api.sendzen.io/v1/messages",
    API_KEY: "sz_sandbox__xlhKm_ERCWgJiCDPzSP0nT2fm3NNkDWauYlvIMkv3pmMA",
    OWNER_PHONE: "919156249716",
    SANDBOX_NUMBER: "15557814168",
}

// ── Google Apps Script Web App URL ────────────
const GOOGLE_SHEET_URL = "https://script.google.com/macros/s/AKfycby9-Dfq1HW_1Bg31GSlNe-QoSnI3_ZUv1zePIRi1tYJD0pA6q8kFGK3JpX8iXyM5NTWdQ/exec"

// ── Firestore collection ──────────────────────
const LEADS_COLLECTION = 'tribeca'

// ─────────────────────────────────────────────
//  Save to Firestore
// ─────────────────────────────────────────────
const saveToFirestore = async (data) => {
    try {
        const docRef = await addDoc(collection(db, LEADS_COLLECTION), {
            ...data,
            createdAt: serverTimestamp(),
        })
        console.log('[Firestore] Saved:', docRef.id)
        return docRef.id
    } catch (err) {
        console.error('[Firestore] Failed:', err)
        return null
    }
}

// ─────────────────────────────────────────────
//  Save to Google Sheet via Apps Script
//  Uses text/plain to skip CORS preflight
// ─────────────────────────────────────────────
const saveToGoogleSheet = async (data) => {
    try {
        const response = await fetch(GOOGLE_SHEET_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain' },
            body: JSON.stringify(data),
        })
        const result = await response.json()

        if (result.success) {
            console.log('[Google Sheet] Row added successfully')
        } else {
            console.error('[Google Sheet] Error:', result.error)
        }
    } catch (err) {
        // Non-blocking — sheet failure never breaks the form submission
        console.error('[Google Sheet] Failed:', err)
    }
}

// ─────────────────────────────────────────────
//  Core WhatsApp sender
// ─────────────────────────────────────────────
const sendMessage = async (messageBody) => {
    const payload = {
        from: SENDZEN_CONFIG.SANDBOX_NUMBER,
        to: SENDZEN_CONFIG.OWNER_PHONE,
        type: "text",
        text: { body: messageBody },
    }

    const response = await fetch(SENDZEN_CONFIG.API_URL, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${SENDZEN_CONFIG.API_KEY}`,
        },
        body: JSON.stringify(payload),
    })

    if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error?.message || `SendZen error: ${response.status}`)
    }

    console.log('[WhatsApp] Message sent')
    return response.json()
}

// ─────────────────────────────────────────────
//  Internal helper
//  Runs Firestore + Sheet + WhatsApp in parallel
//  Sheet & Firestore are non-blocking (catch own errors)
//  WhatsApp throws so form components can show error state
// ─────────────────────────────────────────────
const submitLead = async (leadData, whatsappMessage) => {
    await Promise.all([
        saveToFirestore(leadData),
        saveToGoogleSheet(leadData),
        sendMessage(whatsappMessage),
    ])
}

// ─────────────────────────────────────────────
//  1. Hero Section enquiry
// ─────────────────────────────────────────────
export const notifyHeroEnquiry = async ({ name, phone, email }) => {
    const leadData = {
        name,
        phone,
        email: email || '',
        source: 'Hero Enquiry',
        requestType: 'Enquiry',
        consent: true,
        status: 'New',
        notes: '',
    }

    const message =
        `🏢 *New Hero Enquiry — The Everett*\n\n` +
        `*Name:* ${name}\n` +
        `*Phone:* ${phone}\n` +
        `*Email:* ${email || 'Not provided'}\n\n` +
        `_Source: Hero Section form_`

    return submitLead(leadData, message)
}

// ─────────────────────────────────────────────
//  2. CTA Section enquiry
// ─────────────────────────────────────────────
export const notifyCTAEnquiry = async ({ name, phone, email }) => {
    const leadData = {
        name,
        phone,
        email: email || '',
        source: 'CTA Enquiry',
        requestType: 'Enquiry',
        consent: true,
        status: 'New',
        notes: '',
    }

    const message =
        `🏢 *New CTA Enquiry — The Everett*\n\n` +
        `*Name:* ${name}\n` +
        `*Phone:* ${phone}\n` +
        `*Email:* ${email || 'Not provided'}\n\n` +
        `_Source: CTA Section form_`

    return submitLead(leadData, message)
}

// ─────────────────────────────────────────────
//  3. Brochure / Floor Plan request
// ─────────────────────────────────────────────
export const notifyBrochureRequest = async ({ name, phone, email, requestType = 'Brochure' }) => {
    const leadData = {
        name,
        phone,
        email: email || '',
        source: requestType,
        requestType,
        consent: true,
        status: 'New',
        notes: '',
    }

    const message =
        `📄 *${requestType} Request — The Everett*\n\n` +
        `*Name:* ${name}\n` +
        `*Phone:* ${phone}\n` +
        `*Email:* ${email || 'Not provided'}\n\n` +
        `_Source: ${requestType} popup form_`

    return submitLead(leadData, message)
}